import type { CompanyApplication } from "../../../domain/types/shared";
import type {
  UpdateCompanyInfoRequest,
  GetCompanyInfoRequest,
  RealtimeGraphVertex,
  SearchCompanyUsersRequest,
  SearchCompanyUsersResponse,
  UpdateUserRoleRequest,
  WithdrawCompanyUserRequest,
} from "../../../domain/types/company";
import {
  RealtimeGraphVertexSchema,
  SearchCompanyUsersResponseSchema,
} from "../../../domain/types/company";
import type {
  CompanyInfoResponse,
  LandingUserInfoResponse,
  UserInfoResponse,
} from "../../../domain/types/shared";
import {
  CompanyInfoResponseSchema,
  LandingUserInfoResponseSchema,
  UserInfoResponseSchema,
} from "../../../domain/types/shared";
import type { CompanyRepository } from "../../../domain/repositories/company/company-repository";
import { adminApiService } from "../../api/company/company-api-service";

export class CompanyRepositoryImpl implements CompanyRepository {
  async getCompanyInfo(
    request: GetCompanyInfoRequest,
  ): Promise<CompanyInfoResponse> {
    const response = await adminApiService.getCompanyInfo(request.companyId);

    if (response.data) {
      return CompanyInfoResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "업체 정보 가져오기 실패.");
  }

  async updateCompanyInfo(
    request: UpdateCompanyInfoRequest,
  ): Promise<CompanyInfoResponse> {
    const response = await adminApiService.updateCompanyInfo(request);

    if (response.data) {
      return CompanyInfoResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "업체 정보 수정 실패");
  }

  async updateUserRole(request: UpdateUserRoleRequest): Promise<boolean> {
    const response = await adminApiService.updateUserRole(request);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "유저 역할 수정 실패.");
  }

  async withdrawCompanyUser(
    request: WithdrawCompanyUserRequest,
  ): Promise<boolean> {
    const response = await adminApiService.withdrawCompanyUser(request);

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "회원 탈퇴 처리 실패.");
  }

  async requestCompany(application: CompanyApplication): Promise<LandingUserInfoResponse> {
    const response = await adminApiService.requestCompany(application);

    if (response.data) {
      return LandingUserInfoResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "업체 신청 실패.");
  }

  async getUserByCompanyId(
    request: SearchCompanyUsersRequest,
  ): Promise<SearchCompanyUsersResponse> {
    const response = await adminApiService.getUserByCompanyId(request);

    if (response.data) {
      return SearchCompanyUsersResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "업체 유저 가져오기 실패.");
  }

  async getCompanyRealtimeGraph(
    companyId: number,
  ): Promise<RealtimeGraphVertex[]> {
    const response = await adminApiService.getCompanyRealtimeGraph(companyId);

    if (response.data) {
      return response.data.map((item) => RealtimeGraphVertexSchema.parse(item));
    }

    throw new Error(response.errorMsg || "실시간 그래프 가져오기 실패.");
  }

  async getCompanyAdminInfo(companyId: number): Promise<UserInfoResponse> {
    const response = await adminApiService.getCompanyAdminInfo(companyId);

    if (response.data) {
      return UserInfoResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "업체 어드민 정보 가져오기 실패.");
  }

  async getEmailSettings(
    companyId: number,
  ): Promise<{ data: { senderEmail: string | null; hasPassword: boolean } }> {
    return await adminApiService.getEmailSettings(companyId);
  }

  async updateEmailSettings(
    companyId: number,
    senderEmail: string,
    senderEmailPassword: string,
  ): Promise<{ data: { senderEmail: string | null; hasPassword: boolean } }> {
    return await adminApiService.updateEmailSettings(
      companyId,
      senderEmail,
      senderEmailPassword,
    );
  }

  async updateTossSecretKey(request: {
    companyId: number;
    tossSecretKey: string;
  }): Promise<boolean> {
    const response = await adminApiService.updateTossSecretKey(
      request.companyId,
      request.tossSecretKey,
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "토스페이먼츠 Secret Key 설정 실패.");
  }
}
